// Copyright 2025, University of Maryland, All Rights Reserved
import React from "react"
import { BaseField, type BaseFieldEditorProps } from "./BaseField.tsx"
import { Label } from "@/components/ui/label.tsx"
import { Button } from "@/components/ui/button.tsx"
import { Plus, X } from "lucide-react"
import { Input } from "@/components/ui/input.tsx"

export const EnumFieldEditor = React.memo(({
  field,
  onFieldUpdate,
  ...restProps
}: BaseFieldEditorProps) => {
  const config = field.config
  const options = config.enum_options || []

  const update_config = (config_updates: Record<string, any>) => {
    onFieldUpdate(field, {
      config: { ...config, ...config_updates }
    })
  }

  const add_option = () => {
    const new_options = [...options, '']
    update_config({ enum_options: new_options })
  }

  const update_option = (index: number, value: string) => {
    const new_options = [...options]
    new_options[index] = value
    update_config({ enum_options: new_options })
  }

  const remove_option = (index: number) => {
    const new_options = options.filter((_: string, i: number) => i !== index)
    update_config({ enum_options: new_options })
  }

  return (
    <BaseField field={field} onFieldUpdate={onFieldUpdate} {...restProps}>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">Options</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={add_option}
            disabled={restProps.isLoading}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Option
          </Button>
        </div>

        {options.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground border-2 border-dashed rounded-lg">
            <p className="text-sm">No options configured</p>
            <p className="text-xs">Add options for the dropdown menu</p>
          </div>
        ) : (
          <div className="space-y-2">
            {options.map((option: string, index: number) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  value={option}
                  onChange={(e) => update_option(index, e.target.value)}
                  placeholder={`Option ${index + 1}`}
                  className="text-sm h-8"
                  disabled={restProps.isLoading}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => remove_option(index)}
                  disabled={restProps.isLoading}
                  title="Remove option"
                >
                  <X className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        )}
        
        <p className="text-xs text-muted-foreground">
          Define the available options for this dropdown field
        </p>
      </div>
    </BaseField>
  )
})

EnumFieldEditor.displayName = 'EnumFieldEditor'
